import { useEffect, useRef } from 'react';
import {
  influencerSubscriptionService,
  getInfluencerSubscriptionFromResponse,
} from '../services/influencerSubscriptionService';

const POLL_INTERVAL_MS = 3000;
const ACTIVATION_TIMEOUT_MS = 60000;

function isActivated(sub) {
  return sub != null && sub.planType === 'premium' && sub.isActive === true;
}

/**
 * Poll influencer subscription until premium plan is active or timeout.
 */
export function useInfluencerSubscriptionPolling({ enabled, onActivated, onTimeout }) {
  const cancelledRef = useRef(false);
  const resolvedRef = useRef(false);
  const intervalIdRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const onActivatedRef = useRef(onActivated);
  const onTimeoutRef = useRef(onTimeout);

  onActivatedRef.current = onActivated;
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) return;

    cancelledRef.current = false;
    resolvedRef.current = false;

    const stopPolling = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };

    const resolve = (type, sub) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      cancelledRef.current = true;
      stopPolling();
      if (type === 'activated') {
        onActivatedRef.current?.(sub);
      } else {
        onTimeoutRef.current?.();
      }
    };

    const checkSubscription = async () => {
      if (cancelledRef.current || resolvedRef.current) return;
      try {
        const response = await influencerSubscriptionService.getSubscription({ cacheBust: true });
        if (cancelledRef.current || resolvedRef.current) return;
        const sub = getInfluencerSubscriptionFromResponse(response);
        if (isActivated(sub)) {
          resolve('activated', sub);
        }
      } catch {
        if (cancelledRef.current || resolvedRef.current) return;
      }
    };

    intervalIdRef.current = setInterval(checkSubscription, POLL_INTERVAL_MS);
    checkSubscription();

    timeoutIdRef.current = setTimeout(() => {
      if (cancelledRef.current || resolvedRef.current) return;
      resolve('timeout');
    }, ACTIVATION_TIMEOUT_MS);

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [enabled]);
}
